/**
 * Export helpers for a Deep Explorer deliverable.
 *
 * - downloadSvg — finds the first rendered Mermaid SVG under a DOM scope
 *   and saves it as a .svg file.
 * - printDeliverableAsPdf — opens the current page's print dialog scoped
 *   to the deliverable element via a temporary print-style wrapper.
 *   Users pick "Save as PDF" in the browser's print dialog.
 */

import DOMPurify from "dompurify"

export function downloadSvg(scope: HTMLElement | null, filename: string): boolean {
  if (!scope) return false
  const svg = scope.querySelector("svg")
  if (!svg) return false
  const serializer = new XMLSerializer()
  // Clone + strip any dynamic inline styles that don't serialize well.
  const clone = svg.cloneNode(true) as SVGElement
  if (!clone.getAttribute("xmlns")) {
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg")
  }
  const source = serializer.serializeToString(clone)
  const blob = new Blob(['<?xml version="1.0" standalone="no"?>\n', source], {
    type: "image/svg+xml;charset=utf-8",
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  return true
}

/**
 * Print the deliverable as PDF via the browser's native print dialog.
 * Opens a new window, injects the deliverable HTML + minimal print CSS,
 * triggers print, then closes the window.
 */
export function printDeliverableAsPdf(scope: HTMLElement | null, title: string): boolean {
  if (!scope) return false
  const html = DOMPurify.sanitize(scope.innerHTML)
  const win = window.open("", "_blank", "width=900,height=1000")
  if (!win) return false
  try {
    win.opener = null
  } catch {
    // Some browser/window shims expose opener as readonly.
  }
  win.document.write(`<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${title.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" })[c] ?? c)}</title>
    <style>
      @page { size: A4; margin: 2cm; }
      body {
        font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
        font-size: 11pt;
        line-height: 1.5;
        color: #111;
        max-width: 750px;
        margin: 0 auto;
        padding: 24px;
      }
      h1, h2, h3, h4 { font-weight: 600; margin-top: 1.2em; }
      h1 { font-size: 1.8em; }
      h2 { font-size: 1.35em; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
      h3 { font-size: 1.1em; }
      a { color: #0366d6; }
      code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.9em; background: #f6f8fa; padding: 1px 4px; border-radius: 3px; }
      pre { background: #f6f8fa; padding: 12px; border-radius: 6px; overflow-x: auto; }
      blockquote { border-left: 3px solid #ddd; margin: 1em 0; padding: 0 1em; color: #555; }
      table { border-collapse: collapse; width: 100%; margin: 1em 0; }
      th, td { border: 1px solid #ddd; padding: 6px 10px; text-align: left; vertical-align: top; }
      th { background: #f6f8fa; font-weight: 600; }
      .supContainer { color: #0366d6; font-size: 0.85em; }
      @media print { .no-print { display: none !important; } }
    </style>
  </head>
  <body>${html}</body>
</html>`)
  win.document.close()
  // Give the new window a tick to render, then print.
  setTimeout(() => {
    win.focus()
    win.print()
    // Close after print dialog is dismissed (best-effort).
    setTimeout(() => {
      try {
        win.close()
      } catch {
        // no-op
      }
    }, 500)
  }, 250)
  return true
}
